import { useState } from "react";
import { useDeleteUserMutation } from "../../redux/slices/api/userApi";
import { useNavigate } from "react-router-dom";
import notify from "../common/notify";
import { BiTrash, BiX } from "react-icons/bi";
import { AiOutlineWarning } from "react-icons/ai";

const DeleteProfileModal = ({ isOpen, onClose }) => {
  const [confirmText, setConfirmText] = useState("");
  const [deleteUser, { isLoading }] = useDeleteUserMutation();
  const navigate = useNavigate();

  const handleDelete = async () => {
    if (confirmText !== "DELETE") {
      notify("Please type DELETE to confirm", "error");
      return;
    }

    try {
      const response = await deleteUser().unwrap();
      notify(response.message || "Account deleted successfully!", "success");
      
      // Clear local storage
      localStorage.clear();
      
      // Redirect to home page
      setTimeout(() => {
        navigate("/");
        window.location.reload();
      }, 1000);
    } catch (error) {
      notify(
        error?.data?.message || "Failed to delete account. Please try again.",
        "error"
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-scale-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isLoading}
        >
          <BiX size={24} />
        </button>

        {/* Warning Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
            <AiOutlineWarning className="text-red-600 text-3xl" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
          Delete Your Account
        </h2>

        {/* Warning Message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800 mb-3 font-semibold">
            ⚠️ This action is permanent and cannot be undone!
          </p>
          <ul className="text-sm text-red-700 space-y-2 list-disc list-inside">
            <li>Your profile will be permanently deleted</li>
            <li>All your comments will be removed</li>
            <li>You will be removed from all communities</li>
            <li>Your notifications will be cleared</li>
            <li>You must delete all your projects first</li>
          </ul>
        </div>

        {/* Confirmation Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="font-bold text-red-600">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all"
            placeholder="Type DELETE here"
            disabled={isLoading}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isLoading || confirmText !== "DELETE"}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Deleting...
              </>
            ) : (
              <>
                <BiTrash size={18} />
                Delete Forever
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteProfileModal;
